import { api } from './client.js'
import { endpoints } from './endpoints.js'
import { unwrapList } from '../utils/format.js'

/**
 * Pharmacy catalogue self-service API.
 *
 * - listStock(pharmacyId)   — fetch a pharmacy's current PharmacyStock rows.
 * - downloadTemplate(opts)  — fetch the CSV template as a Blob; caller
 *                             triggers download via createObjectURL.
 * - bulkUpload({ file, pharmacyId }) — multipart POST of the edited CSV.
 *                             Returns the server's summary JSON.
 * - updateStockRow(id, patch) — inline edit a single PharmacyStock row.
 * - deleteStockRow(id)      — remove a stock row (pharmacy stops stocking).
 */

/**
 * Admin convenience: list every pharmacy so the catalogue page can let
 * an admin pick which pharmacy's catalogue to manage. Pharmacy staff
 * don't need this — they're scoped to their own pharmacy on the server.
 */
export async function listPharmacies() {
  const { data } = await api.get(endpoints.pharmacyList)
  return unwrapList(data)
}

export async function listStock({ pharmacyId, search = '', page = 1 } = {}) {
  const params = { page }
  if (pharmacyId) params.pharmacy = pharmacyId
  if (search?.trim()) params.search = search.trim()
  const { data } = await api.get(endpoints.pharmacyStock, { params })
  return unwrapList(data)
}

/**
 * Download the formulary template. Returns a Blob the caller saves via
 * a hidden <a download>. Pass `otcOnly: true` to limit the template to
 * OTC drugs (~213 rows) instead of the full formulary (~1826 rows).
 */
export async function downloadTemplate({ otcOnly = false } = {}) {
  const url = otcOnly
    ? `${endpoints.pharmacyStockTemplate}?include_otc_only=true`
    : endpoints.pharmacyStockTemplate
  const res = await api.get(url, { responseType: 'blob' })
  return res.data
}

/**
 * Upload an edited CSV. Returns the server's response:
 *   { summary: { rows_processed, stock_created, stock_updated,
 *                proposals_created, errors },
 *     errors:    [{row, error}, ...],
 *     proposals: [{row, medication_name, proposal_id}, ...] }
 */
export async function bulkUpload({ file, pharmacyId }) {
  const fd = new FormData()
  fd.append('file', file)
  fd.append('pharmacy_id', String(pharmacyId))
  const { data } = await api.post(endpoints.pharmacyStockBulkUpload, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function updateStockRow(id, patch) {
  const { data } = await api.patch(`${endpoints.pharmacyStock}${id}/`, patch)
  return data
}

export async function deleteStockRow(id) {
  await api.delete(`${endpoints.pharmacyStock}${id}/`)
}

/**
 * Upload (or replace) the product image for a *global* Drug. Admin-only —
 * the DrugViewSet permission is `ReadOnlyForAuthenticated | IsAdminRole`,
 * so non-admins will 403. Pharmacy staff see the image via the read-only
 * `drug_image_url` pass-through on PharmacyStockSerializer.
 *
 * Multipart PATCH so we don't have to send the rest of the Drug fields.
 * Pass `null` to clear the image.
 */
export async function setDrugImage(drugId, file) {
  const fd = new FormData()
  if (file) {
    fd.append('image', file)
  } else {
    fd.append('image', '')
  }
  const { data } = await api.patch(`${endpoints.drugSearch}${drugId}/`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}
